/**
 * Turning a thrown Firebase error into something a user and a support engineer
 * can both act on.
 *
 * Every write screen used to collapse all failures into "Check your connection
 * and retry." The failure this app actually hits is `permission-denied` from the
 * Firestore security rules, which is not a connection problem — so installers
 * were told to retry something that could never succeed, and support had no way
 * to tell the two apart from a screenshot. The Firebase `code` is therefore kept
 * verbatim in the message.
 */

/**
 * The `code` off a thrown Firebase error.
 *
 * Firestore codes are bare (`"permission-denied"`); Auth codes are namespaced
 * (`"auth/wrong-password"`). Both arrive as a `code` property on the error, but
 * neither is typed as such on `unknown`, hence the narrowing here.
 */
export function firebaseErrorCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "code" in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === "string") return code;
  }
  return undefined;
}

/**
 * A user-facing sentence for a failed Firestore operation.
 *
 * `subject` is a capitalized noun phrase for the thing that failed ("The
 * registration"), so the same mapping serves every write screen.
 *
 * The distinction that matters is retryable vs not: `unavailable` is worth
 * retrying, `permission-denied` never is. Saying so stops the support loop where
 * an installer clears their cache and re-authenticates for a rules problem.
 */
export function describeFirebaseError(err: unknown, subject: string): string {
  // Thrown by the helpers in API/firebase.ts before any network call.
  if (err instanceof Error && err.message === "NOT_AUTHENTICATED") {
    return "Your session has expired. Sign in again and retry.";
  }

  const code = firebaseErrorCode(err);

  switch (code) {
    case "permission-denied":
      return (
        `${subject} was refused by the database. This is a permissions problem, ` +
        `not a connection one — retrying, clearing the cache or signing in again ` +
        `will not help. Contact Artome support and quote "permission-denied".`
      );

    case "unauthenticated":
    case "auth/user-token-expired":
      return "Your session has expired. Sign in again and retry.";

    case "unavailable":
    case "deadline-exceeded":
    case "cancelled":
      return `${subject} could not reach the database. Check your connection and retry.`;

    case "invalid-argument":
    case "failed-precondition":
    case "out-of-range":
      return (
        `${subject} was rejected as invalid, so the stored record was not ` +
        `changed. Contact Artome support and quote "${code}".`
      );

    case "resource-exhausted":
      return `${subject} was rate limited. Wait a moment and retry.`;

    default:
      return code
        ? `${subject} failed (${code}). Retry, and contact Artome support with that code if it keeps happening.`
        : `${subject} failed. Retry, and contact Artome support if it keeps happening.`;
  }
}
